import axios from "axios";
import { GENERAL_URL_API } from "../urls";

const FILAS_HOME_URL = `${GENERAL_URL_API}/filasHome`;
const EDITAR_FILAS_URL = `${FILAS_HOME_URL}/editarFilasHomeRecomendados`;

/**
 * Normaliza el valor del campo 'activo' al formato que espera el backend.
 * Acepta booleanos, números o strings ("1"/"0"). Devuelve "1" o "0".
 */
const normalizeActivo = (activo) => {
  if (activo === true || activo === 1 || activo === "1") return "1";
  return "0";
};

/** Fila marcada como estática en BD / API: no debe reordenarse en el admin. */
export const isEstaticoTrue = (val) =>
  val === true || val === 1 || val === "1" || val === "true";

export async function fetchFilasHome() {
  const { data } = await axios.get(`${FILAS_HOME_URL}/verFilasHome`, {
    withCredentials: true,
  });

  return data?.data?.filasHome ?? [];
}

/**
 * Obtiene los libros de una fila específica.
 * @param {string} filaHome - El nombre de la fila de la que se obtendrán los libros.
 * @returns {Promise<Array>} - Una promesa que se resuelve en un arreglo de libros obtenidos de la fila.
 */
export async function fetchLibrosFila(filaHome) {
  const fd = new FormData();
  fd.append("filaHome", filaHome);

  const { data } = await axios.post(`${FILAS_HOME_URL}/verLibrosFila`, fd, {
    withCredentials: true,
  });

  return data?.data ?? [];
}

/**
 * Crea una nueva fila home.
 * @param {Object} options
 * @param {string} options.nombre
 * @param {string} options.descripcion
 * @param {string} options.disponibilidadRegional - lista separada por comas
 * @param {string} [options.filaHomeNameEng]
 * @param {string} [options.descriptionEng]
 * @param {boolean|string|number} [options.activo] - estado por defecto al crear
 */
export async function createFilaHome({
  nombre,
  descripcion,
  disponibilidadRegional,
  filaHomeNameEng = "",
  descriptionEng = "",
  activo = false,
}) {
  const fd = new FormData();
  fd.append("editionType", "añadirFilaHome");
  fd.append("filaHome", nombre);
  fd.append("description", descripcion);
  fd.append("disponibilidadRegional", disponibilidadRegional);
  fd.append("filaHomeNameEng", filaHomeNameEng);
  fd.append("descriptionEng", descriptionEng);
  fd.append("activo", normalizeActivo(activo));

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Edita una fila home existente.
 * @param {Object} options
 * @param {string} options.nombre
 * @param {string} options.descripcion
 * @param {string} options.disponibilidadRegional
 * @param {string} [options.filaHomeNameEng]
 * @param {string} [options.descriptionEng]
 * @param {boolean|string|number} [options.activo]
 */
export async function editFilaHome({
  nombre,
  descripcion,
  disponibilidadRegional,
  filaHomeNameEng = "",
  descriptionEng = "",
  activo,
}) {
  const fd = new FormData();
  fd.append("editionType", "editarFilaHome");
  fd.append("filaHome", nombre);
  fd.append("description", descripcion);
  fd.append("disponibilidadRegional", disponibilidadRegional);
  fd.append("filaHomeNameEng", filaHomeNameEng);
  fd.append("descriptionEng", descriptionEng);
  if (activo !== undefined) {
    fd.append("activo", normalizeActivo(activo));
  }

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Cambia únicamente el estado activo/inactivo de una fila.
 * Atajo para usar desde la tabla sin reenviar todos los datos.
 */
export async function toggleFilaHomeActivo({ nombre, activo, fila = {} }) {
  return editFilaHome({
    nombre,
    descripcion: fila?.description ?? "",
    disponibilidadRegional: fila?.disponibilidadRegional ?? "",
    filaHomeNameEng: fila?.filaHomeNameEng ?? "",
    descriptionEng: fila?.descriptionEng ?? "",
    activo,
  });
}

/**
 * Elimina una fila home existente según su nombre.
 */
export async function deleteFilaHome(nombre) {
  const fd = new FormData();
  fd.append("editionType", "eliminarFilaHome");
  fd.append("filaHome", nombre);

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Agrega un libro a una fila home específica.
 */
export async function agregarLibroFila({ idLibro, nombreFila }) {
  const fd = new FormData();
  fd.append("editionType", "añadirLibro");
  fd.append("idLibro", idLibro);
  fd.append("filaHome", nombreFila);

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Elimina un libro de una fila home específica.
 */
export async function eliminarLibroFila({ idLibro, nombreFila }) {
  const fd = new FormData();
  fd.append("editionType", "eliminarLibro");
  fd.append("idLibro", idLibro);
  fd.append("filaHome", nombreFila);

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

/**
 * Guarda el nuevo orden de las filas home activas.
 *
 * El backend recorre el array y asigna `orden = índice + 1` a cada fila.
 * Las filas inactivas no se incluyen en el array; el backend les asigna
 * `orden = NULL` si no aparecen en la lista.
 *
 * @param {Object} options
 * @param {Array<{filaHome: string}>} options.filasOrdenadas - array de filas activas en el nuevo orden
 */
export async function setOrdenFilaHome({ filasOrdenadas }) {
  const fd = new FormData();
  fd.append("editionType", "reordenarFilas");
  fd.append("orden", JSON.stringify(filasOrdenadas));

  const { data } = await axios.post(EDITAR_FILAS_URL, fd, {
    withCredentials: true,
  });

  return data;
}

export async function fetchPaquetesLibro(idLibro) {
  const fd = new FormData();
  fd.append("idLibro", idLibro);

  const { data } = await axios.post(
    `${GENERAL_URL_API}/paquetes/verPaquetesLibro`,
    fd,
    {
      withCredentials: true,
    }
  );

  return data?.data ?? [];
}
